import { CommonModule, Location } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  QueryList,
  ViewChildren,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { IconModule } from '@aw/shared/components/icon';

import { ButtonComponent } from '@aw/prypco/components/button';
import { GtmService } from '@aw/prypco/services/script-manager';

import { AuthError } from '../../+state';
import { OryAuthFacade } from '../../+state/ory-auth.facade';
import { OryAuthPages } from '../../constants';
import {
  OryFlowType,
  OryMessageCode,
  otpDigitsLength,
} from '../../constants/ory.constant';

@Component({
  selector: 'aw-ory-auth-otp-verify',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent, IconModule],
  templateUrl: './otp-verify.component.html',
  styleUrl: './otp-verify.component.scss',
})
export class OtpVerifyComponent implements OnInit, AfterViewInit {
  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;
  private destroyRef = inject(DestroyRef);
  private location = inject(Location);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private oryAuthFacade = inject(OryAuthFacade);
  private gtmService = inject(GtmService);

  readonly registerRoute = ['/', OryAuthPages.ROOT, OryAuthPages.REGISTER];
  readonly loginRoute = ['/', OryAuthPages.ROOT, OryAuthPages.LOGIN];
  readonly errorCodes = OryMessageCode;
  readonly otpDigitsLength = otpDigitsLength;
  readonly otpDigitsIndexes = Array.from(
    { length: this.otpDigitsLength },
    (_, i) => i,
  );

  email = signal<string>('');
  otpDigits = signal<string[]>(Array(this.otpDigitsLength).fill(''));
  formDisabled = signal<boolean>(false);
  formOtpInvalid = signal<boolean>(false);

  otpForm = new FormGroup({
    otp: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(this.otpDigitsLength),
        Validators.maxLength(this.otpDigitsLength),
      ],
    }),
  });

  // Reactive state from store
  readonly registrationFlow$ = this.oryAuthFacade.registrationFlow$;
  readonly loginFlow$ = this.oryAuthFacade.loginFlow$;

  readonly pendingRegistrationData$ =
    this.oryAuthFacade.pendingRegistrationData$;
  readonly pendingLoginData$ = this.oryAuthFacade.pendingLoginData$;

  readonly isSubmitting$ = this.oryAuthFacade.isSubmitting$;
  readonly submitError$ = this.oryAuthFacade.submitError$;
  readonly session$ = this.oryAuthFacade.session$;
  readonly isResending$ = this.oryAuthFacade.isResending$;
  readonly resendError$ = this.oryAuthFacade.resendError$;
  readonly resendCountdown$ = this.oryAuthFacade.resendCountdown$;
  readonly canResend$ = this.oryAuthFacade.canResend$;
  readonly flowType$ = this.oryAuthFacade.currentFlowType$;

  readonly flowId$ = combineLatest([
    this.registrationFlow$,
    this.loginFlow$,
    this.flowType$,
  ]).pipe(
    map(([registrationFlow, loginFlow, flowType]) => {
      if (flowType === OryFlowType.REGISTRATION) return registrationFlow?.id;
      if (flowType === OryFlowType.LOGIN) return loginFlow?.id;
      return null;
    }),
  );

  readonly error$ = combineLatest([
    this.oryAuthFacade.registrationFlowError$,
    this.oryAuthFacade.loginFlowError$,
    this.submitError$,
    this.resendError$,
  ]).pipe(
    map(
      ([registrationError, loginError, submitError, resendError]) =>
        registrationError || loginError || submitError || resendError,
    ),
    distinctUntilChanged(),
    tap((error: AuthError | null) => {
      if (!error) {
        this.formOtpInvalid.set(false);
        return;
      }

      if (error?.code === OryMessageCode.AccountAlreadyExists) {
        this.formOtpInvalid.set(false);
        this.formDisabled.set(true);
        return;
      }

      this.focusLastOtpDigit();
      this.formOtpInvalid.set(true);
    }),
  );

  protected pendingRegistrationData = toSignal(this.pendingRegistrationData$, {
    initialValue: null,
  });
  protected isSubmitting = toSignal(this.isSubmitting$, {
    initialValue: false,
  });
  protected isResending = toSignal(this.isResending$, {
    initialValue: false,
  });
  protected submitError = toSignal(this.submitError$, {
    initialValue: null,
  });
  protected resendCountdown = toSignal(this.resendCountdown$, {
    initialValue: 0,
  });
  protected canResend = toSignal(this.canResend$, {
    initialValue: true,
  });
  protected error = toSignal(this.error$);

  protected hasSession = toSignal(
    this.session$.pipe(map((session) => !!session)),
    { initialValue: false },
  );

  protected isLoading = computed(
    () => this.isSubmitting() || this.isResending() || this.hasSession(),
  );

  protected formEmail = computed(() => this.pendingRegistrationData()?.email);
  protected resendCountdownFormatted = computed(() => {
    const totalSeconds = this.resendCountdown();
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const secondsFormatted = seconds.toString().padStart(2, '0');
    return `${minutes}:${secondsFormatted}`;
  });

  constructor() {
    combineLatest([
      this.flowId$,
      this.flowType$,
      this.pendingLoginData$,
      this.pendingRegistrationData$,
    ])
      .pipe(take(1))
      .subscribe(([flowId, flowType, loginData, registrationData]) => {
        // Check if flow exists
        if (!flowId || !flowType) {
          this.router.navigate(['/', OryAuthPages.ROOT, OryAuthPages.LOGIN], {
            replaceUrl: true,
          });
          return;
        }

        // Check if we have the required data based on flow type
        const hasRequiredData =
          (flowType === OryFlowType.LOGIN && loginData) ||
          (flowType === OryFlowType.REGISTRATION && registrationData);

        if (!hasRequiredData) {
          // Redirect to appropriate page based on flow type and remove from history
          const route =
            flowType === OryFlowType.REGISTRATION
              ? ['/', OryAuthPages.ROOT, OryAuthPages.REGISTER]
              : ['/', OryAuthPages.ROOT, OryAuthPages.LOGIN];

          this.router.navigate(route, { replaceUrl: true });
        }
      });
  }

  ngOnInit(): void {
    this.oryAuthFacade.clearErrors();

    // Get email from route params
    const emailParam = this.route.snapshot.queryParamMap.get('email');
    if (emailParam) {
      this.email.set(emailParam);
    }

    this.otpForm
      .get('otp')
      ?.valueChanges.pipe(
        filter((value) => !!value && value.length === this.otpDigitsLength),
        debounceTime(300), // Small delay to ensure all digits are processed
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        // Only auto-submit if form is valid and not already submitting
        if (this.otpForm.valid && !this.isSubmitting() && !this.isResending()) {
          this.verify();
        }
      });
  }

  ngAfterViewInit(): void {
    this.focusFirstOtpDigit();
  }

  onDigitInput(event: Event, index: number): void {
    const target = event.target as HTMLInputElement;
    const onlyDigitsRegexp = /[^0-9]/g;
    const value = target.value.replace(onlyDigitsRegexp, '');

    if (value.length > 1) {
      // Handle paste case - distribute digits across inputs
      this.handlePastedValue(value, index);
      return;
    }

    // Update the specific digit
    const currentDigits = [...this.otpDigits()];
    currentDigits[index] = value;
    this.otpDigits.set(currentDigits);
    this.updateFormValue(currentDigits);

    // Move to next input if digit was entered
    if (value && index < this.otpDigitsLength) {
      const nextInput = this.otpInputs.toArray()[index + 1];
      nextInput?.nativeElement.focus();
    }
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    const target = event.target as HTMLInputElement;

    if (event.key === 'Backspace' || event.key === 'Delete') {
      const currentDigits = [...this.otpDigits()];

      if (target.value) {
        currentDigits[index] = '';
        this.otpDigits.set(currentDigits);
        this.updateFormValue(currentDigits);
      } else if (index > 0) {
        const prevInput = this.otpInputs.toArray()[index - 1];
        prevInput?.nativeElement.focus();
        currentDigits[index - 1] = '';
        this.otpDigits.set(currentDigits);
        this.updateFormValue(currentDigits);
      }

      event.preventDefault();
    } else if (event.key === 'ArrowLeft' && index > 0) {
      const prevInput = this.otpInputs.toArray()[index - 1];
      prevInput?.nativeElement.focus();
    } else if (event.key === 'ArrowRight' && index < this.otpDigitsLength) {
      const nextInput = this.otpInputs.toArray()[index + 1];
      nextInput?.nativeElement.focus();
    } else if (
      !event.key.match(/^[0-9]$/) &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey
    ) {
      // Prevent non-digit input
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent, index: number): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    this.handlePastedValue(pastedData, index);
  }

  goBack(): void {
    this.location.back();
  }

  private handlePastedValue(value: string, startIndex: number): void {
    const digits = value
      .replace(/[^0-9]/g, '')
      .split('')
      .slice(0, this.otpDigitsLength);
    const currentDigits = [...this.otpDigits()];

    for (
      let i = 0;
      i < digits.length && startIndex + i < this.otpDigitsLength;
      i++
    ) {
      currentDigits[startIndex + i] = digits[i];
    }

    this.otpDigits.set(currentDigits);
    this.updateFormValue(currentDigits);

    const lastFilledIndex = Math.min(
      startIndex + digits.length - 1,
      this.otpDigitsLength - 1,
    );
    const nextEmptyIndex = currentDigits.findIndex(
      (digit, i) => i > lastFilledIndex && !digit,
    );
    const focusIndex =
      nextEmptyIndex !== -1
        ? nextEmptyIndex
        : Math.min(lastFilledIndex + 1, this.otpDigitsLength - 1);

    setTimeout(() => {
      const targetInput = this.otpInputs.toArray()[focusIndex];
      targetInput?.nativeElement.focus();
    }, 0);
  }

  private updateFormValue(digits: string[]): void {
    const otpValue = digits.join('');
    this.otpForm.patchValue({ otp: otpValue });
    this.formOtpInvalid.set(false);
  }

  verify(): void {
    if (this.otpForm.invalid) return;

    const otpValue = this.otpDigits().join('');
    if (otpValue.length !== this.otpDigitsLength) {
      return;
    }

    combineLatest([this.flowId$, this.flowType$])
      .pipe(
        take(1),
        switchMap(([flowId, flowType]) => {
          if (!flowId || !flowType) {
            throw new Error('No flow data available');
          }

          const formData = {
            code: otpValue,
          };

          this.oryAuthFacade.submitOtp(formData);
          return [];
        }),
      )
      .subscribe();
  }

  resendCode(): void {
    // Check if resend is allowed
    if (!this.canResend()) {
      return;
    }

    // Clear OTP inputs immediately when resend is clicked
    const emptyDigits = Array(this.otpDigitsLength).fill('');
    this.otpDigits.set(emptyDigits);
    this.updateFormValue(emptyDigits);

    // Clear any existing errors
    this.oryAuthFacade.clearErrors();
    this.oryAuthFacade.resendOtp();
  }

  onSignUp(buttonName): void {
    this.gtmServicePush(buttonName);
  }

  gtmServicePush(buttonName: string) {
    const utmParams = this.parseUTMJson();
    this.gtmService.pushEvent({
      event: 'signup_started',
      utm_source: utmParams['utm_source'] || '',
      utm_medium: utmParams['utm_medium'] || '',
      utm_campaign: utmParams['utm_campaign'] || '',
      utm_term: utmParams['utm_term'] || '',
      utm_content: utmParams['utm_content'] || '',
      click_text: buttonName,
      cta_location: 'otp_page',
      timestamp: new Date().toISOString(),
    });
  }

  private parseUTMJson(): { [key: string]: string } {
    const utmRaw = localStorage.getItem('utm') || '{}';
    try {
      const parsed = JSON.parse(utmRaw);
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
      // Failed to parse UTM JSON from localStorage
      return {};
    }
  }

  private focusFirstOtpDigit(): void {
    const firstInput = this.otpInputs?.first;
    if (firstInput) {
      setTimeout(() => firstInput?.nativeElement.focus(), 0);
    }
  }

  private focusLastOtpDigit(): void {
    const lastInput = this.otpInputs?.last;
    if (lastInput) {
      setTimeout(() => lastInput?.nativeElement.focus(), 0);
    }
  }
}
